import * as React from 'react';

import { cn } from '../../lib/utils';
import { CodeInput } from '../CodeInput';

export interface VerificationCodeFieldProps {
  /** Current code value (array of 4 digits) */
  value: string[];
  /** Callback when the code changes */
  onChange: (value: string[]) => void;
  /** Number of digits in the code (default: 4) */
  length?: number;
  /** Whether the field has an error */
  error?: boolean;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether to auto-focus the first input on mount */
  autoFocus?: boolean;
  /** Callback when Enter key is pressed */
  onEnter?: () => void;
  /** Accessible group label. When set, the field is a named `role="group"` for
   * screen readers (individual digit boxes stay nameless). Default: unnamed. */
  ariaLabel?: string;
  /** Optional `data-testid` for the group wrapper (e2e locator). */
  testId?: string;
  /** Additional CSS classes */
  className?: string;
}

/**
 * VerificationCodeField Molecule
 *
 * A group of digit inputs for entering verification codes.
 * Features:
 * - Auto-focus management between inputs
 * - Paste support for full code
 * - Keyboard navigation (backspace)
 * - Error state styling
 *
 * @example
 * ```tsx
 * const [code, setCode] = useState(['', '', '', '']);
 *
 * <VerificationCodeField
 *   value={code}
 *   onChange={setCode}
 *   error={hasError}
 * />
 * ```
 */
export const VerificationCodeField = React.forwardRef<HTMLDivElement, VerificationCodeFieldProps>(
  (
    {
      value,
      onChange,
      length = 4,
      error = false,
      disabled = false,
      autoFocus = false,
      onEnter,
      ariaLabel,
      testId,
      className,
    },
    ref
  ) => {
    const inputRefs = React.useRef<(HTMLInputElement | null)[]>([]);

    // Auto-focus the first input on mount when autoFocus is true
    React.useEffect(() => {
      if (autoFocus) {
        inputRefs.current[0]?.focus();
      }
    }, [autoFocus]);

    // Ensure value array has correct length
    const normalizedValue = React.useMemo(() => {
      const result = [...value];
      while (result.length < length) {
        result.push('');
      }
      return result.slice(0, length);
    }, [value, length]);

    const handleChange = React.useCallback(
      (index: number, digit: string) => {
        const newValue = [...normalizedValue];
        newValue[index] = digit;
        onChange(newValue);
      },
      [normalizedValue, onChange]
    );

    const handleNext = React.useCallback(
      (index: number) => {
        if (index < length - 1) {
          inputRefs.current[index + 1]?.focus();
        }
      },
      [length]
    );

    const handleBackspace = React.useCallback((index: number) => {
      if (index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    }, []);

    const handlePaste = React.useCallback(
      (startIndex: number, pastedValue: string) => {
        const digits = pastedValue.split('').slice(0, length);
        const newValue = [...normalizedValue];

        digits.forEach((digit, i) => {
          const targetIndex = startIndex + i;
          if (targetIndex < length) {
            newValue[targetIndex] = digit;
          }
        });

        onChange(newValue);

        // Focus the input after the last pasted digit, or the last input
        const focusIndex = Math.min(startIndex + digits.length, length - 1);
        setTimeout(() => {
          inputRefs.current[focusIndex]?.focus();
        }, 0);
      },
      [normalizedValue, length, onChange]
    );

    return (
      <div
        ref={ref}
        role={ariaLabel ? 'group' : undefined}
        aria-label={ariaLabel}
        data-testid={testId}
        className={cn('flex flex-col gap-1 items-center py-5 w-full', className)}
      >
        <div className="flex gap-4 items-center">
          {normalizedValue.map((digit, index) => (
            <CodeInput
              key={index}
              value={digit}
              onChange={(value) => handleChange(index, value)}
              onNext={() => handleNext(index)}
              onBackspace={() => handleBackspace(index)}
              onPaste={(value) => handlePaste(index, value)}
              onEnter={onEnter}
              error={error}
              disabled={disabled}
              inputRef={(el) => {
                inputRefs.current[index] = el;
              }}
            />
          ))}
        </div>
      </div>
    );
  }
);

VerificationCodeField.displayName = 'VerificationCodeField';

export default VerificationCodeField;
